import React from 'react';
import ProfileAssistantRow from '../../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../../GeneralProfile/ProfileUserAnswerRow';
import { interpolateI18n } from '../../utils/interpolateI18n';
import { prevStepForUndo } from './dependentsSectionSteps';
import type { DependentsSectionFlow } from './useDependentsSectionFlow';
import type { DependentsStep } from './dependentsSectionTypes';

/** Q&A steps rendered in chat order for the dependent currently being answered. */
const MEMBER_DISPLAY_ORDER: DependentsStep[] = [
  'addName',
  'addKana',
  'addRelationship',
  'addRelationshipOther',
  'addDob',
  'confirmFullName',
  'confirmFullNameKana',
  'confirmRelationship',
  'myNumber',
  'sameHousehold',
  'addressIfNot',
  'sharedLivelihood',
  'estimatedIncome',
  'nonResident',
  'nonResidentReason',
  'remittanceAmount',
  'disabilityStatus',
  'changeDuringYear',
  'changeDate',
  'changeReason',
  'changeReasonOther',
];

function MemberQA({ flow }: { flow: DependentsSectionFlow }) {
  const undoTarget = prevStepForUndo(flow.step, flow.draft);
  const showFieldEdit = flow.expandedReview;
  const inEdit = flow.editIndex !== null;
  return (
    <>
      {MEMBER_DISPLAY_ORDER.map((s) => {
        const noticeBefore =
          s === 'nonResident' && flow.isCommitted('ageCategoryNotice') ? (
            <ProfileAssistantRow>{flow.ageCategoryNoticeText}</ProfileAssistantRow>
          ) : null;
        if (!flow.isCommitted(s)) return <React.Fragment key={s}>{noticeBefore}</React.Fragment>;
        const answer = flow.userAnswerForStep(s);
        return (
          <React.Fragment key={s}>
            {noticeBefore}
            <ProfileAssistantRow>{flow.t(flow.stepQuestionKey(s))}</ProfileAssistantRow>
            <ProfileUserAnswerRow
              showUndo={!inEdit && undoTarget === s}
              onUndo={flow.handleUndo}
              undoLabel={flow.undoLabel}
              showEdit={showFieldEdit}
              onEdit={() => flow.beginEditField(s)}
              editLabel={flow.editLabel}
            >
              {answer}
            </ProfileUserAnswerRow>
          </React.Fragment>
        );
      })}
      {flow.isCommitted('ineligibleNotice') &&
        flow.step !== 'ineligibleNotice' &&
        Boolean(flow.ineligibleNoticeText) && (
          <ProfileAssistantRow>{flow.ineligibleNoticeText}</ProfileAssistantRow>
        )}
    </>
  );
}

export default function DependentsSectionChatThread({ flow }: { flow: DependentsSectionFlow }) {
  const { t } = flow;
  const dependentHeader = (n: number) =>
    interpolateI18n(t('submitDocuments.dependentDeduction.dependents.dependentHeader'), {
      index: String(n),
    });

  const yesLabel = t('submitDocuments.dependentDeduction.spouse.yes');
  const noLabel = t('submitDocuments.dependentDeduction.spouse.no');
  const ynLabel = (v: string) => (v === 'yes' ? yesLabel : v === 'no' ? noLabel : '');

  return (
    <>
      <ProfileAssistantRow>
        {t('submitDocuments.dependentDeduction.dependents.intro')}
      </ProfileAssistantRow>

      {flow.hasDependentsCommitted && (
        <>
          <ProfileAssistantRow>
            {t('submitDocuments.dependentDeduction.dependents.steps.hasDependents')}
          </ProfileAssistantRow>
          <ProfileUserAnswerRow undoLabel={flow.undoLabel}>
            {ynLabel(flow.hasDependentsAnswer)}
          </ProfileUserAnswerRow>
        </>
      )}

      {flow.completed.map((entry, i) => (
        <React.Fragment key={`dep-${i}`}>
          <ProfileAssistantRow>{dependentHeader(i + 1)}</ProfileAssistantRow>
          {flow.editIndex === i ? (
            <MemberQA flow={flow} />
          ) : (
            <ProfileUserAnswerRow
              undoLabel={flow.undoLabel}
              showEdit={flow.showEditOnSummary(i)}
              onEdit={() => flow.handleEditSummary(i)}
              editLabel={flow.editLabel}
            >
              {entry.text}
            </ProfileUserAnswerRow>
          )}
        </React.Fragment>
      ))}

      {flow.addAnotherCommitted && flow.editIndex === null && (
        <>
          <ProfileAssistantRow>
            {t('submitDocuments.dependentDeduction.dependents.steps.addAnother')}
          </ProfileAssistantRow>
          <ProfileUserAnswerRow undoLabel={flow.undoLabel}>
            {ynLabel(flow.addAnotherAnswer)}
          </ProfileUserAnswerRow>
        </>
      )}

      {flow.inMemberFlow && flow.editIndex === null && (
        <>
          <ProfileAssistantRow>{dependentHeader(flow.currentDependentNumber)}</ProfileAssistantRow>
          <MemberQA flow={flow} />
        </>
      )}

      {flow.step === 'ineligibleNotice' && (
        <ProfileAssistantRow animateIn>{flow.ineligibleNoticeText}</ProfileAssistantRow>
      )}
      {flow.step === 'ageCategoryNotice' && !flow.isCommitted('ageCategoryNotice') && (
        <ProfileAssistantRow animateIn>{flow.ageCategoryNoticeText}</ProfileAssistantRow>
      )}

      {flow.step !== 'finalConfirm' &&
        flow.step !== 'ineligibleNotice' &&
        flow.step !== 'ageCategoryNotice' &&
        !flow.isCommitted(flow.step) &&
        flow.step !== 'addAnother' &&
        flow.step !== 'hasDependents' && (
          <ProfileAssistantRow animateIn>{t(flow.stepQuestionKey(flow.step))}</ProfileAssistantRow>
        )}

      {flow.step === 'hasDependents' && !flow.hasDependentsCommitted && (
        <ProfileAssistantRow animateIn>
          {t('submitDocuments.dependentDeduction.dependents.steps.hasDependents')}
        </ProfileAssistantRow>
      )}

      {flow.step === 'addAnother' && !flow.addAnotherCommitted && (
        <ProfileAssistantRow animateIn>
          {t('submitDocuments.dependentDeduction.dependents.steps.addAnother')}
        </ProfileAssistantRow>
      )}

      {flow.step === 'finalConfirm' && flow.editIndex === null && (
        <ProfileAssistantRow animateIn>
          {flow.completed.length > 0
            ? t('submitDocuments.dependentDeduction.dependents.finalConfirm')
            : t('submitDocuments.dependentDeduction.dependents.noDependentsDone')}
        </ProfileAssistantRow>
      )}
    </>
  );
}
